/**
 * Reads EXIF orientation from image file and returns corrected image as data URL
 */
export async function getOrientedImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Get EXIF orientation
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Read orientation from EXIF data
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const orientation = getOrientation(new Uint8Array(arrayBuffer));
        
        // Set canvas dimensions based on orientation
        if (orientation > 4) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        // Transform canvas based on EXIF orientation
        switch (orientation) {
          case 2:
            ctx.transform(-1, 0, 0, 1, img.width, 0);
            break;
          case 3:
            ctx.transform(-1, 0, 0, -1, img.width, img.height);
            break;
          case 4:
            ctx.transform(1, 0, 0, -1, 0, img.height);
            break;
          case 5:
            ctx.transform(0, 1, 1, 0, 0, 0);
            break;
          case 6:
            ctx.transform(0, 1, -1, 0, img.height, 0);
            break;
          case 7:
            ctx.transform(0, -1, -1, 0, img.height, img.width);
            break;
          case 8:
            ctx.transform(0, -1, 1, 0, 0, img.width);
            break;
        }

        // Draw image on canvas with correct orientation
        ctx.drawImage(img, 0, 0);
        
        // Convert canvas to data URL
        resolve(canvas.toDataURL('image/jpeg', 0.95));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Extract orientation value from EXIF data
 */
function getOrientation(buffer: Uint8Array): number {
  const view = new DataView(buffer.buffer);
  
  // Check for JPEG signature
  if (view.getUint16(0, false) !== 0xFFD8) {
    return 1; // Default orientation
  }

  const length = view.byteLength;
  let offset = 2;

  while (offset < length) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    // Check for APP1 marker (EXIF data)
    if (marker === 0xFFE1) {
      const exifLength = view.getUint16(offset, false);
      offset += 2;

      // Check for EXIF signature
      const exifSignature = view.getUint32(offset, false);
      if (exifSignature !== 0x45786966) {
        return 1;
      }

      offset += 6; // Skip EXIF signature and padding

      // Check byte order
      const byteOrder = view.getUint16(offset, false);
      const littleEndian = byteOrder === 0x4949;
      offset += 2;

      // Skip fixed value
      offset += 2;

      // Get offset to IFD
      const ifdOffset = view.getUint32(offset, littleEndian);
      offset += ifdOffset - 8;

      // Get number of directory entries
      const tags = view.getUint16(offset, littleEndian);
      offset += 2;

      // Search for orientation tag (0x0112)
      for (let i = 0; i < tags; i++) {
        const tag = view.getUint16(offset + i * 12, littleEndian);
        if (tag === 0x0112) {
          return view.getUint16(offset + i * 12 + 8, littleEndian);
        }
      }
    } else if ((marker & 0xFF00) !== 0xFF00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }

  return 1; // Default orientation
}
